<script>
import { mapActions, mapGetters } from 'vuex'
import get from 'lodash/get'
import RetailerOffersForm from './retailer-offers-form'
import Status from '~/components/status/status'
import { loadGooglePlaces } from '~/utils/google-places-api.js'

export default {
  name: 'RetailerOffers',
  page: {
    title: 'Retailer Offers',
  },
  components: {
    RetailerOffersForm,
    Status,
  },
  data() {
    return {
      retailer: {},
      offers: [],
      mouseDown: false,
      draggedOfferId: '',
      loading: false,
    }
  },
  computed: {
    ...mapGetters('me', [
      'canCreateOffer',
      'canUpdateOffer',
    ]),
    id() {
      return this.$route.params.id
    },
    title() {
      return `${this.retailer.name} Offers`
    },
    backRoute() {
      return {
        name: 'retailer-details',
        params: { id: this.id },
      }
    },
    isHasOffers() {
      return this.offers && this.offers.length > 0
    },
    isNewOffer() {
      return this.activeTile === 'new'
    },
    offersCount() {
      return get(this.offers, 'length')
    },
    activeTile: {
      get() {
        return this.$route.query.active
      },
      set(active) {
        const query = Object.assign({}, this.$route.query)

        if (active) {
          query.active = active
        } else delete query.active

        this.$router.replace({
          query,
        }).catch(() => {})
      },
    },
  },
  async created() {
    await loadGooglePlaces()
    await this.fetchRetailer()
    await this.fetchRetailerOffers()
    this.scrollToActiveTile()
  },
  mounted() {
    document.addEventListener('mouseup', this.onMouseUp)
    document.addEventListener('mousemove', this.onMouseMove)
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.onMouseUp)
    document.removeEventListener('mousemove', this.onMouseMove)
  },
  methods: {
    ...mapActions('retailer', ['getRetailer']),
    ...mapActions('offer', ['getRetailerOffers']),
    async fetchRetailer() {
      this.loading = true
      const [err, response] = await this.getRetailer(this.id)

      if (err) {
        this.$router.replace(this.backRoute)
      } else {
        this.retailer = response
      }

      this.loading = false
    },
    async fetchRetailerOffers() {
      this.loading = true
      const [, response] = await this.getRetailerOffers(this.id)

      if (response && response.items) {
        this.offers = response.items.sort((a, b) => {
          return a.sortIndex !== b.sortIndex
            ? a.sortIndex - b.sortIndex
            : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        })
      }

      if (!this.isHasOffers) this.activeTile = 'new'

      this.loading = false
    },
    noop() {
      //
    },
    scrollToActiveTile() {
      const activeTile = this.$route.query.active
      if (activeTile && !this.isNewOffer) {
        const tile = document.getElementById(activeTile)
        const main = document.getElementsByClassName('el-main')[0]
        if (tile && main) {
          main.scroll({
            top: tile.offsetTop - 90,
            behavior: 'smooth',
          })
        }
      }
    },
    isActiveOffer(id) {
      return this.activeTile === id
    },
    addNewOffer() {
      this.activeTile = 'new'
    },
    previewOffers() {
      this.$router.push({ name: 'retailer-offers-preview' })
    },
    closeTile() {
      this.activeTile = null
    },
    onMouseUp() {
      this.mouseDown = false
      this.draggedOfferId = ''
    },
    onMouseDown(draggedOfferId) {
      this.mouseDown = true
      this.draggedOfferId = draggedOfferId
    },
    onMouseMove(e) {
      if (!this.mouseDown) {
        return
      }

      const { draggedOfferId } = this
      const offersList = this.offers
      const { dragHandles } = this.$refs
      const offersCount = dragHandles.length
      const tabCoords = dragHandles
        .map(h => {
          const { top, bottom } = h.getBoundingClientRect()

          return { top, bottom }
        })
        .sort((a, b) => a.top - b.top)
      const mousePos = e.clientY
      const currentOfferIndex = offersList.findIndex(
        o => o.id === draggedOfferId
      )
      let newOfferIndex = currentOfferIndex

      for (let i = 0; i < offersCount; i++) {
        const { top, bottom } = tabCoords[i]

        if (
          mousePos === top ||
          mousePos === bottom ||
          (mousePos > top && mousePos < bottom)
        ) {
          newOfferIndex = i
        }
      }

      if (currentOfferIndex !== newOfferIndex) {
        this.replaceOffers(currentOfferIndex, newOfferIndex)
      }
    },
    replaceOffers(currentOfferIndex, newOfferIndex) {
      const newOffersList = [].concat(this.offers)
      const currentOffer = newOffersList[currentOfferIndex]
      const offerToReplace = newOffersList[newOfferIndex]

      newOffersList.splice(currentOfferIndex, 1, offerToReplace)
      newOffersList.splice(newOfferIndex, 1, currentOffer)

      this.offers = newOffersList

      this.saveOffersSort()
    },
    onUpdate() {
      this.closeTile()
      this.fetchRetailerOffers()
    },
    async saveOffersSort() {
      const offers = this.offers.reduce((offers, offer, idx) => {
        if (offer.sortIndex !== idx) {
          offers.push({
            sortIndex: idx,
            id: offer.id,
          })
        }

        return offers
      }, [])

      offers.forEach(({ id, sortIndex }) => {
        const offer = this.offers.find(offer => offer.id === id)
        if (offer) offer.sortIndex = sortIndex
      })

      await Promise.all(
        offers.map(offer => this.$store.dispatch('offer/updateOffer', offer))
      )
    },
  },
}
</script>

<template>
  <main-layout
    :title="title"
    :back="backRoute"
    :loading="loading"
    :class="{ [$style.grab]: mouseDown }"
  >
    <div
      v-if="isHasOffers"
      slot="header"
      :class="$style.headerBtns"
    >
      <el-button
        plain
        @click="previewOffers"
      >
        Preview Offers
      </el-button>
      <el-button
        v-if="canCreateOffer"
        type="primary"
        plain
        icon="el-icon-plus"
        @click="addNewOffer"
      >
        Add New Offer
      </el-button>
    </div>

    <div :class="[$style.retailerOffers, {[$style.newHeaderBlocked]: !isHasOffers}]">
      <el-collapse
        v-model="activeTile"
        accordion
      >
        <el-collapse-item
          v-if="isNewOffer"
          name="new"
        >
          <template #title>
            <div :class="$style.collapseHeader">
              <span :class="$style.offerName">Add New Offer</span>
              <el-button
                type="text"
                :class="$style.action"
              >
                <i
                  :class="[
                    isNewOffer ? 'el-icon-arrow-up' : 'el-icon-arrow-down',
                  ]"
                />
              </el-button>
            </div>
          </template>
          <retailer-offers-form
            :retailer="retailer"
            :offers-count="offersCount"
            @update="onUpdate"
            @cancel="closeTile"
          />
        </el-collapse-item>

        <template v-if="isHasOffers">
          <el-collapse-item
            v-for="offer in offers"
            :id="offer.id"
            :key="offer.id"
            :name="offer.id"
          >
            <template #title>
              <div
                v-if="!isActiveOffer(offer.id)"
                ref="dragHandles"
                :class="$style.dragHandle"
                @mousedown.stop="onMouseDown(offer.id)"
                @click.stop="noop"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="25"
                >
                  <path
                    fill="#626262"
                    d="M3.5 6.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3.5 15.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3.5 24.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM13.5 6.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM13.5 15.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM13.5 24.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                  />
                </svg>
              </div>
              <div :class="$style.collapseHeader">
                <span :class="$style.offerName">
                  {{ offer.name }}
                </span>
                <div :class="$style.actions">
                  <div :class="$style.status">
                    <status
                      :status="offer.active"
                      :inactive-style="{opacity: 0.4}"
                    />
                  </div>
                  <el-button
                    v-if="canUpdateOffer"
                    type="text"
                    :class="$style.action"
                  >
                    <template v-if="!isActiveOffer(offer.id)">
                      Edit
                    </template>
                    <i
                      :class="[
                        isActiveOffer(offer.id)
                          ? 'el-icon-arrow-up'
                          : 'el-icon-arrow-down',
                      ]"
                    />
                  </el-button>
                </div>
              </div>
            </template>
            <retailer-offers-form
              v-if="canUpdateOffer"
              :offer="offer"
              :retailer="retailer"
              :offers-count="offersCount"
              @update="onUpdate"
              @cancel="closeTile"
            />
            <span v-if="!canUpdateOffer">Permission denied</span>
          </el-collapse-item>
        </template>
      </el-collapse>
    </div>
  </main-layout>
</template>

<style lang="scss" module>
.retailerOffers {
  :global {
    .el-collapse,
    .el-collapse-item__wrap {
      border: none;
    }

    .el-collapse-item {
      overflow: hidden;
      background-color: #fff;
      border: 1px solid #e4e7ed;
      border-radius: rem(4px);
      box-shadow: rem(0 2px 12px 0) rgba(0, 0, 0, 0.1);

      .is-active {
        background: #f7f7f7;
        transition: background-color 0.2s;
      }
    }

    .el-collapse-item + .el-collapse-item {
      margin-top: rem(20px);
    }

    .el-collapse-item__arrow {
      display: none;
    }

    .el-collapse-item__header {
      position: relative;
      height: rem(100px);
      user-select: none;
      border: none;
    }

    .el-collapse-item__content {
      padding: 2rem 3rem;
      font-size: inherit;
      border-top: 1px solid #ebeef5;
    }
  }
}

.newHeaderBlocked {
  :global {
    .el-collapse-item__header {
      pointer-events: none;
    }
  }
}

.collapseHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding-left: 2rem;
  margin-right: 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  color: #303133;
}

.headerBtns {
  margin-right: -3rem;
}

.offerName {
  margin-left: 3rem;
}

.dragHandle {
  position: absolute;
  top: -1px;
  bottom: -1px;
  left: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 33px;
  height: calc(100% + 2px);
  cursor: grab;
  background-color: #f7f7f7;
  border: 1px solid #e4e7ed;
  border-radius: rem(4px);
}

.grab * {
  cursor: move;
}

.action {
  float: right;
  margin-right: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #0086ff;
  background-color: transparent;
  border: none;

  i {
    margin-left: 0.5rem;
    font-weight: 600;
  }
}

.actions {
  display: flex;
  justify-items: center;
}

.status {
  padding-right: 2rem;
  font-size: 1rem;
}
</style>
